import React, { useEffect, useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { useModulePermission } from '../../hooks/usePermission';
import { FN111, FN112, FN116, FN117, FN111A, pwn201Api } from '../../services/pwn201Api';
import { usePWN201Mode } from './hooks/usePWN201Mode';
import { usePWN201Record } from './hooks/usePWN201Record';
import { usePWN201Navigation } from './hooks/usePWN201Navigation';
import TabBasicInfo from './components/TabBasicInfo';
import TabShipDetails from './components/TabShipDetails';
import TabHandleDetails from './components/TabHandleDetails';
import TabMessages from './components/TabMessages';
import TabAttachments from './components/TabAttachments';
import TabOffsetDetails from './components/TabOffsetDetails';
import './PWN201Form.css';
import { BatchAcceptanceBridge, pwn201f1AcceptanceManifest } from '../../features/acceptance';

const TABS = [
  { key: '1', label: '基本資料' },
  { key: '2', label: '客訴及抵帳交運資料' },
  { key: '3', label: '客訴處理要求/說明' },
  { key: '4', label: '留言傳送' },
  { key: '5', label: '附件' },
  { key: '6', label: '抵帳交運單' },
];

const PWN201Form: React.FC = () => {
  const permissions = useModulePermission('pwn201f1');
  const invoiceRequestPermissions = useModulePermission('pwn201_invoice_request');
  const { mode, isEditing, enterAdd, enterEdit, exitEdit } = usePWN201Mode();
  const {
    record, formData, shipDetails, handleDetails, messages, attachments, offsetDetails,
    loading, loadRecord, resetForAdd, revertToRecord,
    getField, setField, handleSave, handleDelete,
    setShipDetails, setHandleDetails, setMessages, setAttachments, setOffsetDetails,
    addShipRow, deleteShipRow,
    addHandleRow, deleteHandleRow,
    addMessageRow, deleteMessageRow,
    addAttachmentRow, deleteAttachmentRow,
    addOffsetRow, deleteOffsetRow,
  } = usePWN201Record(
    useCallback((m: 'browse' | 'add' | 'edit') => {
      if (m === 'browse') exitEdit();
    }, [exitEdit])
  );
  const { loadList, navFirst, navPrev, navNext, navLast, allIds, currentIdx } =
    usePWN201Navigation(loadRecord);
  const [activeTab, setActiveTab] = useState('1');
  const [selectedShipIdx, setSelectedShipIdx] = useState<number | null>(null);
  const selectedShipRow = selectedShipIdx !== null ? shipDetails[selectedShipIdx] ?? null : null;
  const [approving, setApproving] = useState(false);
  const [creatingInvoiceRequests, setCreatingInvoiceRequests] = useState(false);

  useEffect(() => { loadList(); }, [loadList]);

  const handleAddClick = () => {
    resetForAdd();
    enterAdd();
  };

  const handleEditClick = () => enterEdit();

  const handleRevertClick = () => {
    revertToRecord();
    exitEdit();
  };

  const handleDeleteClick = () => {
    Modal.confirm({
      title: '確認刪除',
      content: `確認刪除客訴單 ${record?.fn110_01}？`,
      okType: 'danger',
      onOk: handleDelete,
    });
  };

  const isLocked = record?.fn110_80 === 'P' || record?.fn110_80 === 'C';

  // Command2.Click (業務自核): FoxPro confirms via PWU016F2 "請確認核准本資料!"
  // before REPLACE FN110_80 WITH "P"; Command2.Valid disables the button once
  // FN110_80="P". Only the current record is sent — no batch selection here.
  const handleApproveClick = () => {
    if (!record) return;
    Modal.confirm({
      title: '核准確認!',
      content: '請確認核准本資料!',
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        setApproving(true);
        try {
          await pwn201Api.approve(record.fn110_01);
          await loadRecord(record.fn110_01);
          message.success('核准完成!');
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr.response?.data?.error || '核准失敗');
        } finally {
          setApproving(false);
        }
      },
    });
  };

  const handleInvoiceRequestClick = () => {
    if (!record) return;
    Modal.confirm({
      title: '折讓發票請求確認',
      content: '確定依抵帳交運單產生折讓發票請求？',
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        setCreatingInvoiceRequests(true);
        try {
          const result = await pwn201Api.createInvoiceRequests(record.fn110_01);
          await loadRecord(record.fn110_01);
          message.success(`已產生 ${result.invoice_requests.length} 筆折讓發票請求`);
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr.response?.data?.error || '折讓發票請求失敗');
        } finally {
          setCreatingInvoiceRequests(false);
        }
      },
    });
  };
  const canNav = !isEditing;

  // Detail row change handlers
  const changeShipRow = (idx: number, field: keyof FN111, value: unknown) =>
    setShipDetails((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const changeHandleRow = (idx: number, field: keyof FN112, value: unknown) =>
    setHandleDetails((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const changeMessageRow = (idx: number, field: keyof FN116, value: unknown) =>
    setMessages((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const changeAttachmentRow = (idx: number, field: keyof FN117, value: unknown) =>
    setAttachments((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const changeOffsetRow = (idx: number, field: keyof FN111A, value: unknown) =>
    setOffsetDetails((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  return (
    <div className="pwn201-form">
      <div className="pwn201-header">
        <h2>客訴處理記錄</h2>
        {record && (
          <span className={`pwn201-status-badge status-${record.fn110_80}`}>
            {record.fn110_80 === 'P' ? '已核准' : '草稿'}
          </span>
        )}
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', marginBottom: 16 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === tab.key ? '2px solid #1890ff' : '2px solid transparent',
              color: activeTab === tab.key ? '#1890ff' : '#666',
              fontWeight: activeTab === tab.key ? 600 : 400,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: 300 }}>
        {activeTab === '1' && (
          <TabBasicInfo
            formData={formData}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
          />
        )}
        {activeTab === '2' && (
          <TabShipDetails
            formData={formData}
            selectedRow={selectedShipRow}
            rows={shipDetails}
            isEditing={isEditing}
            onAdd={addShipRow}
            onDelete={deleteShipRow}
            onChange={changeShipRow}
            onSelectRow={setSelectedShipIdx}
          />
        )}
        {activeTab === '3' && (
          <TabHandleDetails
            totalCount={formData.fn110_33 ?? handleDetails.length}
            rows={handleDetails}
            isEditing={isEditing}
            onAdd={addHandleRow}
            onDelete={deleteHandleRow}
            onChange={changeHandleRow}
          />
        )}
        {activeTab === '4' && (
          <TabMessages
            totalCount={formData.fn110_36 ?? messages.length}
            rows={messages}
            isEditing={isEditing}
            onAdd={addMessageRow}
            onDelete={deleteMessageRow}
            onChange={changeMessageRow}
          />
        )}
        {activeTab === '5' && (
          <TabAttachments
            totalCount={formData.fn110_37 ?? attachments.length}
            rows={attachments}
            isEditing={isEditing}
            onAdd={addAttachmentRow}
            onDelete={deleteAttachmentRow}
            onChange={changeAttachmentRow}
          />
        )}
        {activeTab === '6' && (
          <TabOffsetDetails
            rows={offsetDetails}
            isEditing={isEditing}
            onAdd={addOffsetRow}
            onDelete={deleteOffsetRow}
            onChange={changeOffsetRow}
          />
        )}
      </div>

      {/* Action Bar */}
      <div className="pwn201-action-bar">
        {/* Navigation */}
        <button onClick={navFirst} disabled={!canNav || currentIdx <= 0} title="最前">|◀</button>
        <button onClick={navPrev} disabled={!canNav || currentIdx <= 0} title="上一筆">◀</button>
        <button onClick={navNext} disabled={!canNav || currentIdx >= allIds.length - 1} title="下一筆">▶</button>
        <button onClick={navLast} disabled={!canNav || currentIdx >= allIds.length - 1} title="最後">▶|</button>

        <div className="divider" />

        {/* CRUD */}
        {!isEditing ? (
          <button
            className="btn-primary"
            onClick={handleAddClick}
            disabled={!permissions?.canCreate}
            title="新增"
          >
            新增
          </button>
        ) : (
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={loading}
            title="存檔"
          >
            {loading ? '存檔中...' : '存檔'}
          </button>
        )}

        {!isEditing ? (
          <button
            onClick={handleEditClick}
            disabled={!record || isLocked || !permissions?.canEdit}
            title="修改"
          >
            修改
          </button>
        ) : (
          <button onClick={handleRevertClick} title="還原">還原</button>
        )}

        <button
          className="btn-danger"
          onClick={handleDeleteClick}
          disabled={!record || isEditing || isLocked || !permissions?.canDelete}
          title="刪除"
        >
          刪除
        </button>

        <button
          className="btn-approve"
          onClick={handleApproveClick}
          disabled={!record || isEditing || isLocked || approving || !permissions?.canApprove}
          title="核准"
        >
          {approving ? '核准中...' : '核准'}
        </button>

        {invoiceRequestPermissions.canApprove && (
          <button
            onClick={handleInvoiceRequestClick}
            disabled={!record || isEditing || creatingInvoiceRequests || offsetDetails.length === 0}
            title="產生折讓發票請求"
          >
            {creatingInvoiceRequests ? '發票請求產生中...' : '產生折讓發票請求'}
          </button>
        )}

        <div className="divider" />
        <span style={{ fontSize: 12, color: '#999' }}>
          {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : ''}
        </span>
      </div>
      <BatchAcceptanceBridge
        manifest={pwn201f1AcceptanceManifest}
        records={record ? [record] : []}
        selectedKeys={record?.fn110_01 ? [record.fn110_01] : []}
        keyOf={(item) => item.fn110_01}
        queryExact={(key) => pwn201Api.get(key)}
        lastApiResponse={record}
        loading={loading}
        onAutoFill={navFirst}
        autoFillDisabled={allIds.length === 0}
        screenDraft={{ master: formData, shipDetails, handleDetails, messages, attachments, offsetDetails }}
      />
    </div>
  );
};

export default PWN201Form;
